// Study-scoped tokens for the canonical DICOM CDN.
//
// The backend returns `canonical_cdn_token` with every study metadata / bbox
// response, only after checking the caller may see that study. The CDN edge
// function rejects object requests without a valid token for the URL's
// `/dicom/<study_uid>/` prefix (a2z-develop/dev-data-import#114).
//
// The token rides in the URL as `?t=<token>`, not a header: a custom header
// would force a CORS preflight for every slice URL. The backend keeps a
// study's token identical for hours, so image URLs (Cornerstone cache keys)
// stay stable across metadata refetches.
// Token format: `v1:<exp>:<study_uid>:<sig>` — all characters are legal in a
// query string, so it is appended unencoded.

const tokensByStudy = new Map<string, string>();

/** Remember the token from a metadata/bbox response. Ignores absent values. */
export function registerCdnToken(token: unknown): void {
  if (typeof token !== "string") return;
  const parts = token.split(":");
  if (parts.length !== 4 || parts[0] !== "v1" || !parts[2]) return;
  tokensByStudy.set(parts[2], token);
}

/** `<cdnBaseUrl>/<s3Key>`, plus `?t=<token>` when a token is known for its study. */
export function canonicalObjectUrl(cdnBaseUrl: string, s3Key: string): string {
  const url = `${cdnBaseUrl}/${s3Key}`;
  const match = /^dicom\/([^/]+)\//.exec(s3Key);
  const token = match ? tokensByStudy.get(match[1]) : undefined;
  return token ? `${url}?t=${token}` : url;
}

/** Test-only reset. */
export function clearCdnTokens(): void {
  tokensByStudy.clear();
}
